<template>
  <div class="preview-section">
    <div v-if="selectedElement" class="card card-preview">
      <cardHeader :elementLabel="elementLabel" :elementType="selectedElement.el_Type" mode="Preview" />
      <div class="card-body">

        <div
          v-if="showReport && results.score == computedMaxScore && results.error === 0 && selectedElement.feedback.correct"
          class="PositiveFeebackContainer">
          {{ selectedElement.feedback.correct }}
        </div>
        <div v-else-if="showReport && selectedElement.feedback.incorrect" class="NegativeFeebackContainer">
          {{ selectedElement.feedback.incorrect }}
        </div>

        <h3>{{ selectedElement.el_Text }}</h3>
     
        <div class="quill-editor" ref="quillEditor" v-show="hasQuillContent"></div>

        <div class="block-options mt-3">
          <div v-for="(option, index) in displayedOptions" :key="option.id"
            :class="['block-option', isSelected(option.id) ? 'selected' : '']"
            :style="computedBlockStyle"
            @click="toggleOption(option.id)">
            <div class="option-content">
              <span v-if="getIndexType !== 'none'" class="option-index">{{ getOptionIndex(index) }}</span>
              <div class="option-text" v-html="option.richText"></div>
              <span v-if="isSelected(option.id)" class="check-mark">✅</span>
            </div>
          </div>
        </div>

        <!-- Hint -->
        <div v-if="displayHintButton" class="hintContainer mt-3">
          <button class="btn btn-sm btn-primary" @click="showHint = true">Hint!</button>
          <div v-if="showHint" class="hintMessage mt-2">{{ selectedElement.tip }}</div>
        </div>
      </div>

      <!-- Report -->
      <div class="ReportContainer" v-if="showReport">
        <h5>Results:</h5>
        <p><strong>Your Answers:</strong> {{ results.selectedOptions.join(', ') }}</p>
        <p><strong>Score:</strong> {{ results.score }}</p>
        <p><strong>Max Score:</strong> {{ computedMaxScore }}</p>
        <p><strong>Penalties:</strong> {{ results.error }}</p>
        <p><strong>Hint used:</strong> {{ results.hint }}</p>


      </div>

      <div class="card-footer text-end">
        <button class="btn btn-sm btn-primary" @click="submitAnswers">Submit</button>
        <button v-if="showReport" class="btn btn-sm btn-console ms-2" @click="showReport = false">Close Report</button>
        <button class="btn btn-sm btn-secondary ms-2" @click="resetSelections">Reset</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, watchEffect } from 'vue';
import Quill from 'quill';
import cardHeader from '../default/cardHeader.vue';
import { useTestStore } from '../../../stores/testStore';

const store = useTestStore();
const selectedElement = computed(() => store.selectedElement);
const elementLabel = computed(() => store.selectedElementLabel);
const isMultiple = computed(() => selectedElement.value?.multiple === true);
const displayHintButton = computed(() => !!selectedElement.value?.tip);

const studentAnswers = ref(isMultiple.value ? [] : null);
const showHint = ref(false);
const showReport = ref(false);
const results = ref({ score: 0, error: 0, mark: 0, selectedOptions: [], hint: false });

const quillEditor = ref(null);
let quillInstance = null;
const hasQuillContent = ref(false);

const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const displayedOptions = ref([]);

// Ajuster dynamiquement la hauteur du conteneur Quill
const adjustQuillHeight = () => {
  if (quillInstance) {
    const contentHeight = quillInstance.root.scrollHeight;
    quillEditor.value.style.overflow = 'hidden';
  }
};

onMounted(() => {
  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      readOnly: true,
      modules: { toolbar: false }
    });
    
    if (selectedElement.value?.el_RichText) {
      quillInstance.root.innerHTML = selectedElement.value.el_RichText;
      hasQuillContent.value = selectedElement.value.el_RichText.trim() !== '' && 
                             selectedElement.value.el_RichText !== '<p><br></p>';
    }
    
    adjustQuillHeight();
  }

  const options = selectedElement.value?.options || []
  displayedOptions.value = selectedElement.value?.randomized
    ? shuffleArray([...options])
    : [...options];
});

// Observer uniquement el_RichText pour les mises à jour en temps réel
watch(
  () => selectedElement.value?.el_RichText,
  (newValue) => {
    if (quillInstance && newValue !== quillInstance.root.innerHTML) {
      quillInstance.root.innerHTML = newValue || '';
      hasQuillContent.value = newValue && newValue.trim() !== '' && newValue !== '<p><br></p>';
      adjustQuillHeight();
    }
  }
);

watchEffect(() => {
  if (!selectedElement.value) return;
  
  studentAnswers.value = isMultiple.value ? [] : null;
  showReport.value = false;
  results.value = { score: 0, error: 0, mark: 0, selectedOptions: [], hint: false };

  const options = selectedElement.value?.options || []
  displayedOptions.value = selectedElement.value?.randomized
    ? shuffleArray([...options])
    : [...options];
});

const toggleOption = (id) => {
  if (!isMultiple.value) {
    studentAnswers.value = id;
  } else {
    const index = studentAnswers.value.indexOf(id);
    if (index === -1) studentAnswers.value.push(id);
    else studentAnswers.value.splice(index, 1);
  }
};

const isSelected = (id) => {
  return isMultiple.value ? studentAnswers.value.includes(id) : studentAnswers.value === id;
};

const resetSelections = () => {
  studentAnswers.value = isMultiple.value ? [] : null;
  showHint.value = false;
  showReport.value = false;
  results.value = {
    score: 0,
    error: 0,
    mark: 0,
    selectedOptions: [],
    hint: false
  };

  if (selectedElement.value?.randomized) {
    displayedOptions.value = shuffleArray([...selectedElement.value.options]);
  } else {
    displayedOptions.value = [...selectedElement.value.options];
  }
};

const computedMaxScore = computed(() => {
  if (!selectedElement.value) return 1;
  if (selectedElement.value.multiple) {
    return selectedElement.value.options
      .filter(opt => opt.isCorrect && Number(opt.weight) > 0)
      .reduce((sum, opt) => sum + Number(opt.weight), 0);
  } else {
    const correctOpt = selectedElement.value.options.find(opt => opt.isCorrect);
    return correctOpt ? Number(correctOpt.weight || 1) : 1;
  }
});

const submitAnswers = () => {
  if (!selectedElement.value) return;
  if (!studentAnswers.value || (Array.isArray(studentAnswers.value) && studentAnswers.value.length === 0)) {
    alert('Please choose an answer!');
    return;
  }

  const selectedIds = isMultiple.value ? studentAnswers.value : [studentAnswers.value];

  results.value.selectedOptions = [];
  results.value.score = 0;
  results.value.error = 0;

  selectedElement.value.options.forEach(opt => {
    if (selectedIds.includes(opt.id)) {
      results.value.selectedOptions.push(stripHtml(opt.richText));
      const weight = parseInt(opt.weight ?? 1) || 0;
      if (opt.isCorrect) results.value.score += weight;
      else results.value.error += weight;
    }
  });

  const maxScore = computedMaxScore.value || 1;
  results.value.mark = ((results.value.score / maxScore) || 0).toFixed(2);
  results.value.hint = showHint.value;
  showReport.value = true;
};

const stripHtml = (html) => html.replace(/<[^>]*>?/gm, '').trim();

// Index type computed
const getIndexType = computed(() => {
  return selectedElement.value?.blockStyle?.indexType || 'letters';
});

// Generate option index (A, B, C or 1, 2, 3)
const getOptionIndex = (index) => {
  const indexType = getIndexType.value;

  if (indexType === 'letters') {
    return String.fromCharCode(65 + index) + ' - '; // A, B, C...
  } else if (indexType === 'numbers') {
    return (index + 1) + ' - '; // 1, 2, 3...
  }
  return '';
};

// Computed style for block options
const computedBlockStyle = computed(() => {
  const style = selectedElement.value?.blockStyle;
  if (!style) return {};

  const textAlign = style.textAlign || 'left';

  const cssStyle = {
    borderColor: style.borderColor || '#cccccc',
    borderWidth: `${style.borderWidth || 1}px`,
    borderRadius: `${style.borderRadius || 8}px`,
    width: `${style.width || 100}%`,
    minHeight: `${style.minHeight || 100}px`,
    textAlign: textAlign,
    fontSize: `${style.fontSize || 16}px`,
    color: style.fontColor || '#000000',
    padding: `${style.padding || 16}px`,
    margin: `${style.margin || 8}px`,
    display: 'flex',
    justifyContent: textAlign === 'center' ? 'center' : (textAlign === 'right' ? 'flex-end' : 'flex-start')
  };

  // Gradient priority over solid background
  if (style.gradientColor && style.gradientColor !== '') {
    const direction = style.gradientDirection || 'to right';
    cssStyle.background = `linear-gradient(${direction}, ${style.backgroundColor || '#ffffff'}, ${style.gradientColor})`;
  } else {
    cssStyle.backgroundColor = style.backgroundColor || '#ffffff';
  }

  if (style.backgroundImage) {
    cssStyle.backgroundImage = `url(${style.backgroundImage})`;
    cssStyle.backgroundSize = style.backgroundSize || 'cover';
    cssStyle.backgroundRepeat = style.backgroundRepeat || 'no-repeat';
    cssStyle.backgroundPosition = 'center';
  }

  return cssStyle;
});
</script>

<style scoped>
.block-option {
  border: 1px solid #ccc;
  padding: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
}

.block-option.selected {
  background-color: #cce5ff;
  border-color: #58595b;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}

.block-option:hover {
  border-width: 2px;
  border-color: #8999a9;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}

.quill-editor {
  min-height: max-content!important;
  height: auto!important;
  border: 1px solid #ccc;
  padding: 10px;
  margin-bottom: 1rem;
  overflow: hidden !important;
  transition: height 0.3s ease;
}

.hintMessage {
  border: green 1px solid;
  border-radius: 5px;
  padding: 10px;
  background-color: rgb(222, 235, 222);
}

.ReportContainer {
  border: grey 1px solid;
  border-radius: 3px;
  padding: 20px;
  background-color: rgb(54, 63, 67);
  color: greenyellow;
}

.PositiveFeebackContainer {
  background-color: rgb(222, 246, 224);
  padding: 10px;
  border: 1px green dotted;
  border-radius: 5px;
}

.NegativeFeebackContainer {
  background-color: rgb(246, 234, 235);
  padding: 10px;
  border: 1px red dotted;
  border-radius: 5px;
}

.btn-console {
  background-color: #4e3636;
  color: white;
}

.block-option .option-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  justify-content: inherit;
}

.option-index {
  font-weight: 600;
  color: inherit;
  flex-shrink: 0;
  min-width: 30px;
  font-size: inherit;
  line-height: inherit;
}

.option-text {
  flex: 1;
}

.option-text :deep(p) {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
}

.option-text :deep(*) {
  font-size: inherit !important;
}

.check-mark {
  
  margin-left: 8px;
  flex-shrink: 0;
  animation: checkAppear 0.3s ease-in-out;
}

@keyframes checkAppear {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
